import { FC, ReactNode, useState, Suspense, useEffect, useCallback } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { graphql, useQueryLoader, PreloadedQuery, usePreloadedQuery } from 'react-relay';
import { useDispatch } from 'react-redux';

//components
import { FormattedPrice } from 'dibs-buyer-price';
import { Tooltip } from 'dibs-elements/exports/Tooltip';
import { shippingMethodDescriptions } from '../messages/pdpShippingInfoMessages';
import { PdpSharedShippingInfoLink } from './PdpSharedShippingInfoLink';

import { FREE_SHIPPING_AND_COST } from 'dibs-search-product-tile/exports/constants';
import { useShippingPrice } from 'dibs-search-product-tile/exports/useShippingPrice';
import { getUserGeoInfo } from 'dibs-regional-info/exports/regionalInfoHelpers';
import { useShippingType, ShippingType } from './hooks/useShippingType';
import { excludeShippingPromos } from './excludeShippingPromos';
import { PdpSharedPromoCode } from '../PdpSharedPromoCode/PdpSharedPromoCode';
import PdpSharedShippingInfoLoading from './PdpSharedShippingInfoLoading';
import { updateRelayVariables } from '../../actions/sharedRelayVariableActions';
import { PdpSharedLowerShippingInfo } from '../PdpSharedLowerShippingInfo/PdpSharedLowerShippingInfo';
import { useUserShippingRegionsUrlLabels } from '../PdpSharedLowerShippingInfo/hooks/useUserShippingRegionsUrlLabels';

import styles from './PdpSharedShippingInfo.scss';

import {
    PdpSharedShippingInfoQuery as QueryType,
    PdpSharedShippingInfoQuery$variables,
    PdpSharedShippingInfoQuery$data,
} from './__generated__/PdpSharedShippingInfoQuery.graphql';

export const PdpSharedShippingInfoQuery = graphql`
    query PdpSharedShippingInfoQuery(
        $itemId: String!
        $userId: String!
        $fetchRegionalInfo: Boolean = false
        $userZipCode: String = ""
        $userCountryCode: String = ""
        $isTrade: Boolean = false
        $fetchItem: Boolean = false
        $fetchUser: Boolean = false
        $isClient: Boolean = false
        $itemQuantity: Int
        $shippingRegions: [String] = []
        $calculateLowerShippingInfo: Boolean = false
    ) {
        viewer {
            ...PdpSharedShippingHolidayDelivery_viewer
            ...PdpSharedShippingInfoLink_viewer
            ...PdpSharedPromoCode_viewer
            ...PdpSharedLowerShippingInfo_viewer
                @arguments(
                    fetchRegionalInfo: $fetchRegionalInfo
                    userZipCode: $userZipCode
                    userCountryCode: $userCountryCode
                )
                @include(if: $calculateLowerShippingInfo)
            geography {
                countries {
                    name
                    abbreviation
                }
            }
            relevantShipping(
                itemId: $itemId
                buyerId: $userId
                quantity: $itemQuantity
                zipCode: $userZipCode
                countryCode: $userCountryCode
            ) {
                quoteRequestStatus
                shipmentQuote {
                    ...PdpSharedShippingInfoLink_shipmentQuote
                    handlingTimeDisplay(displayShort: true)
                    subTotalAmount {
                        convertedAmountList {
                            currency
                            amount
                        }
                    }
                    buyerServiceMethodDisplay
                    showQuoteAsPending
                    serviceMethod {
                        serviceType
                        serviceMethodType
                    }
                }
                shipment {
                    shippingAddress {
                        displayCityStateCountry
                    }
                }
            }

            ...useShippingPrice_viewer
                @arguments(
                    fetchRegionalInfo: $fetchRegionalInfo
                    userZipCode: $userZipCode
                    userCountryCode: $userCountryCode
                )
            ...useShippingType_viewer
                @arguments(
                    userId: $userId
                    itemId: $itemId
                    fetchRegionalInfo: $fetchRegionalInfo
                    userZipCode: $userZipCode
                    userCountryCode: $userCountryCode
                    itemQuantity: $itemQuantity
                )

            itemRead(itemIds: [$itemId]) @include(if: $fetchItem) {
                ... on Item {
                    serviceId
                    ...useShippingPrice_item
                        @arguments(
                            buyerId: $userId
                            fetchShippingCostInfo: true
                            itemQuantity: $itemQuantity
                        )
                    ...PdpSharedLowerShippingInfo_item
                        @arguments(
                            isTrade: $isTrade
                            buyerId: $userId
                            fetchShippingCostInfo: true
                            itemQuantity: $itemQuantity
                            userCountryCode: $userCountryCode
                            shippingRegions: $shippingRegions
                        )
                        @include(if: $calculateLowerShippingInfo)
                    ...useShippingType_item @arguments(userId: $userId, itemQuantity: $itemQuantity)
                    ...PdpSharedShippingInfoLink_item
                    ...PdpSharedShippingInfoWrapper_item
                }
            }

            user(userId: $userId) @include(if: $fetchUser) {
                serviceId
                addressBook {
                    proAddress {
                        displayCountry
                        zipCode
                    }
                    personalAddress {
                        displayCountry
                        zipCode
                    }
                    addressList {
                        displayCountry
                        zipCode
                    }
                }
                ...PdpSharedPromoCode_user
                ...PdpSharedShippingInfoLink_user
            }
        }
    }
`;

export const AVAILABLE_NOW = 'available-now';

export const REQUEST_STATUS = {
    REQUEST_QUOTE: 'requestQuote',
    CUSTOM_PROVIDED: 'customQuoteProvided',
    CUSTOM_REQUESTED: 'customQuoteRequested',
};

const getRegionName = ({
    quoteRequestStatus,
    addressBook,
    countries,
    customQuoteShippingAddress,
}: {
    quoteRequestStatus?: string | null;
    addressBook: NonNullable<PdpSharedShippingInfoQuery$data['viewer']['user']>['addressBook'];
    countries: NonNullable<PdpSharedShippingInfoQuery$data['viewer']['geography']>['countries'];
    customQuoteShippingAddress?: string | null;
}): string => {
    const { personalAddress, proAddress, addressList } = addressBook || {};
    const { displayCountry, zipCode } = proAddress || personalAddress || addressList?.[0] || {};
    const { countryCode, zipCode: regionalZipCode } = getUserGeoInfo() || {};

    const regionalCountryName = countries?.find(c => c?.abbreviation === countryCode)?.name || '';
    const displayCountryName = displayCountry || regionalCountryName || '';
    const displayZipCode = (displayCountry ? zipCode : regionalZipCode) || '';

    if (quoteRequestStatus === REQUEST_STATUS.CUSTOM_PROVIDED) {
        return customQuoteShippingAddress || '';
    }

    if (quoteRequestStatus === REQUEST_STATUS.CUSTOM_REQUESTED) {
        return customQuoteShippingAddress || '';
    }

    return displayCountryName + (displayZipCode ? ` ${displayZipCode}` : '');
};

const getShippingTypeMessage = ({
    price,
    fullPrice,
    currency,
    shippingType,
}: {
    price: number | null;
    fullPrice: number;
    currency: string;
    shippingType: ShippingType;
}): ReactNode => {
    return (
        <FormattedMessage
            id="pdp.PdpSharedShippingInfoComponent.shippingType"
            defaultMessage="{shippingType, select,
                free {<wrapper>Free Shipping:</wrapper>}
                free_discounted {<wrapper>Free Shipping:</wrapper> {discountPrice}}
                paid {{shippingPrice}}
                paid_discounted {{discountPrice} {styledShippingPrice}}
                other {}
            }"
            values={{
                shippingType,
                discountPrice: (
                    <span className={styles.discountedPrice}>
                        <FormattedPrice currency={currency} value={fullPrice} />
                    </span>
                ),
                shippingPrice: (
                    <span>
                        <FormattedPrice currency={currency} value={price || 0} />
                    </span>
                ),
                styledShippingPrice: (
                    <span className={styles.salePrice}>
                        <FormattedPrice currency={currency} value={price || 0} />
                    </span>
                ),
                wrapper: message => <span className={styles.freeShippingMessage}>{message}</span>,
                Wrapper: message => <div>{message}</div>,
            }}
        />
    );
};

type PdpSharedShippingInfoProps = {
    itemId: string;
    userId: string;
    currency: string;
    leadTime?: string;
    renderTooltip?: boolean;
    preLinkQuoteDetails?: ReactNode;
    postLinkQuoteDetails?: ReactNode;
    postLinkNoQuoteDetails?: ReactNode;
    title?: ReactNode;
    displayInline?: boolean;
    isTrade?: boolean;
    isClient?: boolean;
    itemQuantity?: number | null;
    userZipCode?: string | null;
    userCountryCode?: string | null;
    children?: ReactNode;
    calculateLowerShippingInfo?: boolean;
};

type PdpSharedShippingInfoComponentProps = {
    queryReference: PreloadedQuery<QueryType>;
    currency: string;
    leadTime?: string;
    renderTooltip?: boolean;
    preLinkQuoteDetails?: ReactNode;
    postLinkQuoteDetails?: ReactNode;
    postLinkNoQuoteDetails?: ReactNode;
    title?: ReactNode;
    displayInline?: boolean;
    reloadQuery?: (arg: PdpSharedShippingInfoQuery$variables) => void;
    itemQuantity?: number | null;
    children?: ReactNode;
    calculateLowerShippingInfo?: boolean;
};

export const PdpSharedShippingInfoComponent: FC<PdpSharedShippingInfoComponentProps> = ({
    currency,
    leadTime,
    renderTooltip,
    preLinkQuoteDetails,
    postLinkQuoteDetails,
    postLinkNoQuoteDetails,
    title,
    displayInline,
    queryReference,
    reloadQuery,
    itemQuantity,
    children,
    calculateLowerShippingInfo = false,
}) => {
    const [showSpinner, setShowSpinner] = useState(true);
    const [tooltipHover, setTooltipHover] = useState(false);
    const { viewer } = usePreloadedQuery<QueryType>(PdpSharedShippingInfoQuery, queryReference);
    const { relevantShipping, geography, itemRead, user } = viewer || {};
    const item = itemRead?.[0] || null;
    const { serviceId: itemId } = item || {};
    const { shipment, shipmentQuote, quoteRequestStatus } = relevantShipping || {};

    const {
        subTotalAmount,
        buyerServiceMethodDisplay: shippingMethodDisplay,
        handlingTimeDisplay,
        showQuoteAsPending,
        serviceMethod,
    } = shipmentQuote || {};

    const customQuoteShippingAddress = shipment?.shippingAddress?.displayCityStateCountry;

    const shippingType = useShippingType({ viewer, item, currency });

    const price = useShippingPrice({
        viewer,
        item,
        currency,
        type: FREE_SHIPPING_AND_COST,
    });

    const fullPrice =
        subTotalAmount?.convertedAmountList?.find(p => p?.currency === currency)?.amount || 0;

    const showQuoteDetails = quoteRequestStatus !== 'requestQuote';

    let overviewMessage;
    if (!shipmentQuote || quoteRequestStatus === REQUEST_STATUS.REQUEST_QUOTE) {
        overviewMessage = (
            <FormattedMessage
                id="pdp.PdpSharedBlurbShippingCustomQuote.overviewRequestQuote"
                defaultMessage="Rates vary by destination and complexity."
            />
        );
    } else if (quoteRequestStatus === REQUEST_STATUS.CUSTOM_REQUESTED || showQuoteAsPending) {
        overviewMessage = (
            <span data-tn="quote-requested">
                <FormattedMessage
                    id="pdp.PdpSharedBlurbShippingCustomQuote.overviewPendingDetails"
                    defaultMessage="Quote pending to {customQuoteShippingAddress}."
                    values={{ customQuoteShippingAddress }}
                />
            </span>
        );
    } else {
        const { serviceType, serviceMethodType } = serviceMethod || {};
        const shippingMethodDescription =
            serviceType && serviceMethodType
                ? (shippingMethodDescriptions as Record<
                      string,
                      Record<string, ReactNode | undefined> | undefined
                  >)[serviceType]?.[serviceMethodType]
                : '';

        overviewMessage = (
            <span data-tn="prequote-overview">
                <FormattedMessage
                    id="pdp.PdpSharedShippingInfo.prequoteOverview"
                    defaultMessage="{shippingType} <tooltip>{method} Shipping</tooltip> to {region}, arrives in {handlingTime}{isAvailable, select,
                        true {.}
                        other {, excluding production time.}
                    }"
                    values={{
                        shippingType: getShippingTypeMessage({
                            price,
                            fullPrice,
                            currency,
                            shippingType,
                        }),
                        method: shippingMethodDisplay,
                        region: getRegionName({
                            quoteRequestStatus,
                            customQuoteShippingAddress,
                            countries: geography?.countries || [],
                            addressBook: user?.addressBook || null,
                        }),
                        handlingTime: handlingTimeDisplay,
                        isAvailable: leadTime === AVAILABLE_NOW,
                        tooltip: content => (
                            <span
                                className={styles.hoverable}
                                onMouseEnter={() => setTooltipHover(true)}
                                onMouseLeave={() => setTooltipHover(false)}
                            >
                                {content}
                                {renderTooltip && shippingMethodDescription && (
                                    <Tooltip isVisible={tooltipHover} direction="bottom">
                                        {shippingMethodDescription}
                                    </Tooltip>
                                )}
                            </span>
                        ),
                    }}
                />
            </span>
        );
    }

    useEffect(() => {
        if (showSpinner) {
            const timer = setTimeout(() => setShowSpinner(false), 2000);
            return () => clearTimeout(timer);
        }
        return () => {};
    }, [showSpinner]);

    const shippingInfo = (
        <span data-tn="pdp-shared-shipping-info">
            <FormattedMessage
                id="pdp.PdpSharedShippingInfo.infoContainer"
                defaultMessage={`{showTitle, select,
                    true {{title}: {content}}
                    other {{content}}
                }`}
                values={{
                    showTitle: price !== 0 && !!title,
                    title,
                    content: (
                        <>
                            <PdpSharedPromoCode
                                onlyShippingPromos
                                excludeShippingPromos={excludeShippingPromos(shippingType)}
                                itemId={itemId || ''}
                                viewer={viewer}
                                user={user || null}
                                userId={user?.serviceId || ''}
                                displayInfo={null}
                                currency={currency}
                            />
                            {overviewMessage} {showQuoteDetails && preLinkQuoteDetails}
                            <PdpSharedShippingInfoLink
                                currency={currency}
                                shipmentQuote={shipmentQuote}
                                userId={user?.serviceId || ''}
                                item={item}
                                itemId={itemId || ''}
                                user={user || null}
                                viewer={viewer}
                                showQuoteDetails={showQuoteDetails}
                                quoteRequestStatus={quoteRequestStatus}
                                onSuccessfulPreQuoteRequest={reloadQuery}
                                displayInline={displayInline && showQuoteDetails}
                                itemQuantity={itemQuantity}
                            />
                            {showQuoteDetails && postLinkQuoteDetails}
                            {!showQuoteDetails && postLinkNoQuoteDetails}
                        </>
                    ),
                }}
            />
            {children}
            {/* PdpSharedLowerShippingInfo can't be passed as children because shipping cost calculation hapens on client side graphql call  */}
            {calculateLowerShippingInfo && (
                <PdpSharedLowerShippingInfo viewer={viewer} item={item} currency={currency} />
            )}
        </span>
    );

    return showSpinner ? (
        <PdpSharedShippingInfoLoading title={title}>{children}</PdpSharedShippingInfoLoading>
    ) : (
        shippingInfo
    );
};

export const PdpSharedShippingInfo: FC<PdpSharedShippingInfoProps> = ({
    isClient = false,
    itemId,
    userId,
    isTrade = false,
    currency,
    leadTime,
    renderTooltip,
    preLinkQuoteDetails,
    postLinkQuoteDetails,
    postLinkNoQuoteDetails,
    title,
    displayInline,
    itemQuantity,
    userCountryCode,
    userZipCode,
    children,
    calculateLowerShippingInfo,
}) => {
    const [queryReference, loadQuery] = useQueryLoader<QueryType>(PdpSharedShippingInfoQuery);
    const dispatch = useDispatch();
    const shippingRegions = useUserShippingRegionsUrlLabels();

    const reloadQuery = useCallback(
        (variables: PdpSharedShippingInfoQuery$variables) => {
            loadQuery({
                ...variables,
                itemId: itemId || '',
                userId: userId || '',
                isTrade,
                fetchUser: !!userId,
                fetchItem: !!itemId,
                isClient,
                itemQuantity,
                userCountryCode,
                userZipCode,
                shippingRegions,
                calculateLowerShippingInfo,
            });
            dispatch(
                updateRelayVariables({
                    updateItemPricing: true,
                    updatedUserCountryCode: variables.userCountryCode,
                })
            );
        },
        [
            loadQuery,
            dispatch,
            itemId,
            userId,
            isTrade,
            isClient,
            itemQuantity,
            userCountryCode,
            userZipCode,
            shippingRegions,
            calculateLowerShippingInfo,
        ]
    );

    useEffect(() => {
        if (isClient) {
            loadQuery({
                itemId: itemId || '',
                userId: userId || '',
                isTrade,
                fetchUser: !!userId,
                fetchItem: !!itemId,
                isClient,
                fetchRegionalInfo: false,
                userZipCode,
                userCountryCode,
                itemQuantity,
                shippingRegions,
                calculateLowerShippingInfo,
            });
        }
    }, [
        loadQuery,
        isClient,
        itemId,
        userId,
        isTrade,
        itemQuantity,
        userZipCode,
        userCountryCode,
        shippingRegions,
        calculateLowerShippingInfo,
    ]);

    return queryReference ? (
        <Suspense
            fallback={
                <PdpSharedShippingInfoLoading title={title}>
                    {children}
                </PdpSharedShippingInfoLoading>
            }
        >
            <PdpSharedShippingInfoComponent
                queryReference={queryReference}
                renderTooltip={renderTooltip}
                currency={currency}
                leadTime={leadTime || ''}
                title={title}
                displayInline={displayInline}
                preLinkQuoteDetails={preLinkQuoteDetails}
                postLinkQuoteDetails={postLinkQuoteDetails}
                postLinkNoQuoteDetails={postLinkNoQuoteDetails}
                reloadQuery={reloadQuery}
                itemQuantity={itemQuantity}
                calculateLowerShippingInfo={calculateLowerShippingInfo}
            >
                {children}
            </PdpSharedShippingInfoComponent>
        </Suspense>
    ) : (
        <PdpSharedShippingInfoLoading title={title}>{children}</PdpSharedShippingInfoLoading>
    );
};
